import moment from 'moment';

import { GuaranteeInsuranceType } from 'components/atoms/bidding_guarantee_insurance';
import useCanUploadContract from 'hooks/useCanUploadContract';
import { Situations } from 'services/situations';
import { BiddingData } from 'store/modules/bidding_store/types';
import { BulletinBiddingsTypes } from 'store/modules/bulletins_store/types';

const CODIGOS_AGRUPAMENTO_SEM_COTACAO = ['SUSS', 'ADIS', 'CANCE', 'SUS', 'REVO', 'ANULA'];

const normalizeSituacao = (value?: string) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const AGRUPAMENTOS_SEM_COTACAO = Situations.filter((situation) =>
  CODIGOS_AGRUPAMENTO_SEM_COTACAO.includes(situation.nome),
).map((situation) => normalizeSituacao(situation.descricao));

export interface GuaranteeInsurance {
  show: boolean;
  type: GuaranteeInsuranceType;
  showQuoteButton: boolean;
  biddingId: number;
}

function useGuaranteeInsurance(
  bidding?: BiddingData | BulletinBiddingsTypes | null,
): GuaranteeInsurance {
  const canSeeExecucaoInsurance = useCanUploadContract();

  const requiresGarantiaProposta = Boolean(bidding?.garantia_proposta);
  const requiresGarantiaContrato = Boolean(bidding?.garantia_contrato);

  const guaranteeDeadlines = [bidding?.datahora_prazo, bidding?.data_validade].filter(Boolean);

  const isWithinGuaranteeDeadline =
    guaranteeDeadlines.length === 0 ||
    guaranteeDeadlines.some((date) => moment(date).isAfter(moment()));

  const type: GuaranteeInsuranceType = (() => {
    if (requiresGarantiaProposta && requiresGarantiaContrato)
      return isWithinGuaranteeDeadline ? 'participacao' : 'execucao';
    return requiresGarantiaContrato ? 'execucao' : 'participacao';
  })();

  const showGuaranteeInsurance =
    requiresGarantiaContrato || (requiresGarantiaProposta && isWithinGuaranteeDeadline);

  const situacaoBloqueiaCotacao = AGRUPAMENTOS_SEM_COTACAO.includes(
    normalizeSituacao(bidding?.bidding_grouping?.descricao),
  );

  const showQuoteButton =
    !situacaoBloqueiaCotacao &&
    (isWithinGuaranteeDeadline ? requiresGarantiaProposta : requiresGarantiaContrato);

  const canSeeGuaranteeInsurance = type === 'execucao' ? canSeeExecucaoInsurance : true;

  return {
    show: canSeeGuaranteeInsurance && showGuaranteeInsurance,
    type,
    showQuoteButton,
    biddingId: bidding ? Number(bidding.bidding_id) || Number(bidding.id) : 0,
  };
}

export default useGuaranteeInsurance;
